/**
 * The change ticker — **the single biggest latency mitigation available.**
 *
 * That claim is about timing, not about layout. A turn is ~50 seconds and the DM spends the
 * first thirty of them resolving before it writes a word, but `change` events stream out of
 * that phase as the engine applies them. So the player watches `−12 health`,
 * `injury: laceration, left forearm`, `raiders reputation −5` tick in while the prose does not
 * yet exist. The alternative is a spinner for the same thirty seconds.
 *
 * **One component, two modes, one data shape.** `live` renders `turn/store.ts`'s `changes` as
 * they arrive; the default renders the identical array off a stored `Chapter`. They are not
 * two components with a shared stylesheet — a chapter that streamed in must look exactly like
 * the same chapter after a reload, and the only way to guarantee that is for there to be
 * nothing to diverge. The mode changes three things and no more: entry animation, the live
 * region, and the label.
 *
 * Expansion shows `data` — the machine-facing half of a `Change`. One row open at a time, and
 * the panel renders under the whole row rather than inside it, so opening something never
 * reflows the row above it (a ticker that jumps while it is still filling is worse than no
 * ticker).
 */
import { useState } from 'react';
import type { Change } from '@/api/generated';
import { ChangeIcon } from '@/features/story/ChangeIcon';
import {
  changeFieldKey,
  familyLabelKey,
  formatChangeValue,
  traitsOf,
  weightOf,
  type ChangeWeight,
} from '@/features/story/changeFamilies';
import { useT } from '@/i18n/context';

interface ChangeTickerProps {
  changes: readonly Change[];
  /**
   * A turn is streaming these in right now. Adds the entry animation and a polite live
   * region; changes nothing about what is rendered.
   */
  live?: boolean;
}

const WEIGHT_CLASS: Record<ChangeWeight, string> = {
  quiet: 'text-fg-faint',
  normal: 'text-fg-muted',
  // The only red in the feed. It has to stay that way to keep meaning anything.
  grave: 'text-[color:var(--color-vital-health)]',
};

export function ChangeTicker({ changes, live = false }: ChangeTickerProps) {
  const t = useT();
  const [open, setOpen] = useState<number | null>(null);

  if (changes.length === 0) return null;

  const expanded = open !== null ? changes[open] : undefined;

  return (
    <div className="mt-6">
      <ul
        // Additions only: a screen-reader user waiting out Phase A hears the changes land,
        // which is the same information the animation gives everyone else. Historical
        // chapters are not a live region — they are not changing.
        aria-live={live ? 'polite' : undefined}
        aria-relevant={live ? 'additions' : undefined}
        aria-label={live ? t('ui.change.live_label') : t('ui.change.label')}
        className="flex flex-wrap gap-x-4 gap-y-1.5 font-mono"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {changes.map((change, index) => {
          const traits = traitsOf(change.kind);
          const weight = weightOf(change);
          return (
            <li
              // Arrival order is the identity. The array is append-only while a turn streams,
              // so the index is stable for the lifetime of every row that uses it.
              key={index}
              className={live ? 'motion-safe:animate-[tick-in_320ms_ease-out_both]' : undefined}
            >
              <button
                type="button"
                aria-expanded={open === index}
                aria-controls={open === index ? 'change-detail' : undefined}
                onClick={() => setOpen(open === index ? null : index)}
                className={`hover:text-fg flex items-center gap-2 text-left transition-colors ${WEIGHT_CLASS[weight]} ${weight === 'grave' ? 'font-medium' : ''}`}
              >
                <ChangeIcon glyph={traits.glyph} />
                {/* The family is the glyph for anyone who can see it, and this for anyone
                    who cannot. `sr-only` rather than a `title`, which phones never show. */}
                <span className="sr-only">{t(familyLabelKey(traits.family))}: </span>
                <span>{change.summary}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {expanded ? <ChangeDetail change={expanded} /> : null}
    </div>
  );
}

/** `Change.data` is `dict[str, Any]` on the wire, so this renders whatever it is given. */
function ChangeDetail({ change }: { change: Change }) {
  const t = useT();
  const entries = Object.entries(change.data);

  return (
    <dl
      id="change-detail"
      className="border-rule text-fg-faint mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-l pl-4 font-mono"
      style={{ fontSize: 'var(--text-meta)' }}
    >
      {entries.length === 0 ? (
        <dd className="col-span-2">{t('ui.change.no_detail')}</dd>
      ) : (
        entries.map(([key, value]) => {
          // Translated where the field is one the engine is known to write, humanized where
          // it is not — the same shape `formatChangeValue` takes to the value beside it.
          // Neither half of a row may go blank because the backend grew a field.
          const labelKey = changeFieldKey(key);
          return (
            <div key={key} className="col-span-2 grid grid-cols-subgrid">
              <dt>{labelKey === null ? key.replaceAll('_', ' ') : t(labelKey)}</dt>
              <dd className="text-fg-muted break-words">{formatChangeValue(value)}</dd>
            </div>
          );
        })
      )}
    </dl>
  );
}
